"use client";

import { useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Fade,
  Zoom,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  PictureAsPdf,
  Close,
  CheckCircle,
  Image as ImageIcon,
  DragIndicator,
} from "@mui/icons-material";

interface ImageFile {
  file: File;
  preview: string;
  id: string;
}

export default function ImageToPdfConverter() {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const validImageTypes = ["image/jpeg", "image/jpg", "image/png"];
    const newImages: ImageFile[] = [];

    Array.from(files).forEach((file) => {
      if (validImageTypes.includes(file.type)) {
        const preview = URL.createObjectURL(file);
        newImages.push({
          file,
          preview,
          id: Math.random().toString(36).substr(2, 9),
        });
      } else {
        setError(
          `${file.name} is not a supported image format. Please use JPG, JPEG, or PNG.`
        );
      }
    });

    if (newImages.length > 0) {
      setImages((prev) => [...prev, ...newImages]);
      setError(null);
      setSuccess(false);
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeImage = (id: string) => {
    setImages((prev) => {
      const updated = prev.filter((img) => img.id !== id);
      const removed = prev.find((img) => img.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
    setError(null);
    setSuccess(false);
  };

  const clearAll = () => {
    images.forEach((img) => URL.revokeObjectURL(img.preview));
    setImages([]);
    setError(null);
    setSuccess(false);
    const fileInput = document.getElementById(
      "file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const convertToPdf = async () => {
    if (images.length === 0) {
      setError("Please select at least one image to convert.");
      return;
    }

    setConverting(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      images.forEach((img) => {
        formData.append("images", img.file);
      });

      const response = await axios.post(
        process.env.NEXT_PUBLIC_API_URL + "/image-to-pdf",
        formData,
        {
          timeout: 600000,
          responseType: "blob",
        }
      );

      // Get the PDF blob
      const blob = new Blob([response.data]);

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `converted-images-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess(true);

      // Clear images after successful conversion
      setTimeout(() => {
        clearAll();
      }, 2000);
    } catch (err) {
      let errorMessage = "Failed to convert images to PDF";
      if (axios.isAxiosError(err) && err.response) {
        try {
          const contentType = String(err.response.headers["content-type"] ?? "");
          if (contentType.includes("application/json")) {
            const errorText = await new Response(err.response.data).text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorMessage;
          } else {
            errorMessage = `Server error: ${err.response.status}`;
          }
        } catch {
          errorMessage = err.message || errorMessage;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setConverting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Upload Area */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: "3px dashed",
            borderColor: dragActive ? "primary.main" : "grey.300",
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            bgcolor: dragActive ? "primary.50" : "grey.50",
            cursor: "pointer",
            transition: "all 0.3s ease",
            position: "relative",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "primary.50",
            },
          }}
          onClick={() => document.getElementById("file-input")?.click()}
        >
          <input
            id="file-input"
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png"
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: "none" }}
          />

          <CloudUpload
            sx={{
              fontSize: 80,
              color: dragActive ? "primary.main" : "grey.400",
              mb: 2,
            }}
          />

          <Typography
            variant="h6"
            component="p"
            gutterBottom
            fontWeight={600}
            color="text.primary"
          >
            {dragActive
              ? "Drop your images here"
              : "Click to upload or drag and drop"}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Supported formats: JPG, JPEG, PNG
          </Typography>

          <Button
            variant="contained"
            startIcon={<ImageIcon sx={{ color: "#ffffff" }} />}
            sx={{
              mt: 3,
              color: "#ffffff",
              backgroundColor: "#5558d3",
              "&:hover": {
                backgroundColor: "#4448c2",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              document.getElementById("file-input")?.click();
            }}
          >
            Select Images
          </Button>
        </Box>

        {/* Selected Images Preview */}
        {images.length > 0 && (
          <Fade in={images.length > 0}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  Selected Images ({images.length})
                </Typography>
                <Button
                  size="small"
                  startIcon={<Delete />}
                  onClick={clearAll}
                  color="error"
                  variant="outlined"
                >
                  Clear All
                </Button>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                  gap: 2,
                  maxHeight: 400,
                  overflowY: "auto",
                  p: 2,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                }}
              >
                {images.map((img, index) => (
                  <Zoom
                    in
                    key={img.id}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        position: "relative",
                        aspectRatio: "1",
                        overflow: "hidden",
                        borderRadius: 2,
                        "&:hover .delete-button": {
                          opacity: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: `url(${img.preview})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      />

                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          left: 4,
                          bgcolor: "rgba(0,0,0,0.6)",
                          color: "white",
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          fontSize: "0.75rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <DragIndicator sx={{ fontSize: 14 }} />
                        {index + 1}
                      </Box>

                      <IconButton
                        className="delete-button"
                        size="small"
                        onClick={() => removeImage(img.id)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(255,255,255,0.9)",
                          opacity: 0,
                          transition: "opacity 0.2s",
                          "&:hover": {
                            bgcolor: "error.main",
                            color: "white",
                          },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>

                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          bgcolor: "rgba(0,0,0,0.7)",
                          color: "white",
                          px: 1,
                          py: 0.5,
                          fontSize: "0.7rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {img.file.name}
                      </Box>
                    </Paper>
                  </Zoom>
                ))}
              </Box>
            </Box>
          </Fade>
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            PDF created successfully! Your download should start automatically.
          </Alert>
        )}

        {/* Convert Button */}
        <Box sx={{ mt: 4 }}>
          {converting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<PictureAsPdf sx={{ color: "#ffffff" }} />}
            onClick={convertToPdf}
            disabled={converting || images.length === 0}
            style={{
              color: "#ffffff",
            }}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {converting
              ? "Converting..."
              : `Convert ${images.length > 0 ? `${images.length} Image${images.length > 1 ? "s" : ""}` : ""} to PDF`}
          </Button>

          {images.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Chip
                icon={<ImageIcon />}
                label={`${images.length} image${images.length > 1 ? "s" : ""} selected`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`~${(images.reduce((acc, img) => acc + img.file.size, 0) / 1024 / 1024).toFixed(2)} MB`}
                variant="outlined"
              />
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
